import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { Container, Form, Row, Col } from "react-bootstrap";
import SvgIcon from '@material-ui/core/SvgIcon';
import Lock from '../icons/lock.svg';
import { GoogleCaptcha } from "../../Common/SubComponents/GoogleCaptcha";
import { SelectInput, TextInput, StyledRadioInput } from "../../../Helpers/Inputs";
import GetStateListForAflac from '../Helpers/StateListForAflac';
import { KENTUCKY, CALIFORNIA, CONNECTICUT, DISTRICT_OF_COLUMBIA, MINNESOTA, PENNSYLVANIA, VERMONT } from '../../../Helpers/Constants/States';
import { InputMaskPhone } from "../../../Helpers/Formatters";
import { customValidators, object, string, ref } from '../../../Helpers/Validators/customValidators';
import BackNext from '../../Common/SubComponents/BackNext';

const employmentStatusExcludedState = [CALIFORNIA, CONNECTICUT, DISTRICT_OF_COLUMBIA, MINNESOTA, PENNSYLVANIA, VERMONT];

export default function InsuredInfo(props) {

    const skipCaptcha = process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

    const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
        validationSchema: InsuredInfo.ValidationSchema,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const handleValueChange = (field, value) => {
        setValue(field, value);
        props.onFieldChange(field, value);
        triggerValidation(field);
    };

    const [validCaptcha, setValidCaptcha] = useState(skipCaptcha ? true : null);

    const handleCaptchaChange = (responseToken) => {
        if (responseToken) {
            setValidCaptcha(true);
        }
    };

    useEffect(() => {
        if (props.basicInfo) {
            setValue('state', props.basicInfo.state);
            setValue('emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);
            props.onStepFieldChange('state', props.basicInfo.state, 'emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);
        }
    }, []);

    useEffect(() => {
        register({ name: 'pin' });
        setValue('pin', props.formState.pin);

        register({ name: 'pinConfirm' });
        setValue('pinConfirm', props.formState.pinConfirm);

        register({ name: 'firstName' });
        setValue('firstName', props.formState.firstName);

        register({ name: 'middleInitial' });
        setValue('middleInitial', props.formState.middleInitial);

        register({ name: 'lastName' });
        setValue('lastName', props.formState.lastName);

        register({ name: 'street' });
        setValue('street', props.formState.street);

        register({ name: 'city' });
        setValue('city', props.formState.city);

        register({ name: 'state' });
        setValue('state', props.basicInfo.state);

        register({ name: 'zip' });
        setValue('zip', props.formState.zip);

        register({ name: 'corporateCityLimits' });
        setValue('corporateCityLimits', props.formState.corporateCityLimits);

        register({ name: 'phoneNumber' });
        setValue('phoneNumber', props.formState.phoneNumber);

        register({ name: 'emailAddress' });
        setValue('emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);

        register({ name: 'employer' });
        setValue('employer', props.formState.employer);

        register({ name: 'employmentStatus' });
        setValue('employmentStatus', props.formState.employmentStatus);
    }, [register]);

    const onSubmit = () => {
        if (validCaptcha) {
            props.onNext();
        } else {
            setValidCaptcha(false);
        }
    };

    const getDisabled = (validCaptcha === null || props.formState.pin === '' || props.formState.pinConfirm === '' || props.formState.firstName === '' || props.formState.lastName === '' || props.formState.street === '' || props.formState.city === '' || props.formState.state === ''
        || props.formState.zip === '' || props.formState.phoneNumber === '' || props.formState.emailAddress === '' || props.formState.employer === '' ||
        (props.formState.state && !employmentStatusExcludedState.includes(props.formState.state) && props.formState.employmentStatus === '') ||
        (props.formState.state === KENTUCKY && props.formState.corporateCityLimits === ''));

    return (
        <Container fluid className='border-class'>
            <Row className="InsuredInfo justify-content-center">
                <Col>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row className="justify-content-center mt-4">
                            <Col lg={5}>
                                <div className="text-center mb-2 pt-4"><SvgIcon component={Lock} className="small-icons" color="primary" fontSize="large" /></div>
                                <div className="text-center form-title">Let's Begin. Safety first.</div>
                                <div className="text-center mb-4"><p className="section-title">Creating a PIN will secure your personal information.</p></div>

                                {!skipCaptcha ? (
                                    <Row className="justify-content-center">
                                        <GoogleCaptcha handleCaptchaChange={handleCaptchaChange} showError={validCaptcha === false} />
                                    </Row>
                                ) : (
                                    <Row className="justify-content-center border my-2">
                                        <span className="text-black-50 my-2">CAPTCHA Disabled By Config</span>
                                    </Row>
                                )}
                                <p className="enter-pin">
                                    Enter and Confirm a PIN to be able to return to your session. All information is secure and confidential.
                                </p>

                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <TextInput
                                                type="password"
                                                name="pin"
                                                id="pin"
                                                label="4 Digit Pin"
                                                value={props.formState.pin}
                                                onChange={e => handleValueChange('pin', e.target.value)}
                                                errors={errors && errors.pin}
                                                inputRef={register}
                                                inputProps={{
                                                    maxLength: 4,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <TextInput
                                                type="password"
                                                name="pinConfirm"
                                                id="pinConfirm"
                                                label="Confirm your Pin"
                                                value={props.formState.pinConfirm}
                                                onChange={e => handleValueChange('pinConfirm', e.target.value)}
                                                errors={errors && errors.pinConfirm}
                                                inputRef={register}
                                                inputProps={{
                                                    maxLength: 4,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group>
                                    <TextInput
                                        name="firstName"
                                        id="firstName"
                                        label="First Name"
                                        value={props.formState.firstName}
                                        onChange={e => handleValueChange('firstName', e.target.value)}
                                        errors={errors.firstName}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 20,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="middleInitial"
                                        id="middleInitial"
                                        label="Middle Initial"
                                        value={props.formState.middleInitial}
                                        onChange={e => handleValueChange('middleInitial', e.target.value)}
                                        errors={errors.middleInitial}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 2,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="lastName"
                                        id="lastName"
                                        label="Last Name"
                                        value={props.formState.lastName}
                                        onChange={e => handleValueChange('lastName', e.target.value)}
                                        errors={errors.lastName}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 50,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <p className="section-title">Street Address</p>
                                    <TextInput
                                        name="street"
                                        id="street"
                                        label="Street Address"
                                        value={props.formState.street}
                                        onChange={e => handleValueChange('street', e.target.value)}
                                        errors={errors.street}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 40,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="city"
                                        id="city"
                                        label="City"
                                        value={props.formState.city}
                                        onChange={e => handleValueChange('city', e.target.value)}
                                        errors={errors.city}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 40,
                                        }}
                                    />
                                </Form.Group>

                                <Row>
                                    <Col>
                                        <Form.Group>
                                            <SelectInput
                                                name="state"
                                                id="state"
                                                value={props.formState.state}
                                                onChange={(e) => handleValueChange('state', e.target.value)}
                                                label="State"
                                                selectOptions={GetStateListForAflac()}
                                                errors={errors && errors.state}
                                                inputRef={register}
                                                disabled={true}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <TextInput
                                                name="zip"
                                                id="zip"
                                                label="Zip"
                                                value={props.formState.zip}
                                                onChange={e => handleValueChange('zip', e.target.value)}
                                                errors={errors.zip}
                                                inputRef={register}
                                                inputProps={{
                                                    maxLength: 5,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {props.formState.state === KENTUCKY &&
                                    <Form.Group>
                                        <p className="section-title">Do you live in the corporate city limits?</p>
                                        <StyledRadioInput
                                            name="corporateCityLimits"
                                            id="corporateCityLimits"
                                            value={props.formState.corporateCityLimits}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('corporateCityLimits', e)}
                                            errors={errors && errors.corporateCityLimits}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </Form.Group>
                                }

                                <Form.Group>
                                    <TextInput
                                        name="phoneNumber"
                                        id="phoneNumber"
                                        label="Primary Phone Number"
                                        value={props.formState.phoneNumber}
                                        onChange={e => handleValueChange('phoneNumber', e.target.value)}
                                        errors={errors.phoneNumber}
                                        inputRef={register}
                                        InputProps={{
                                            inputComponent: InputMaskPhone,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="emailAddress"
                                        id="emailAddress"
                                        label="Email"
                                        value={props.formState.emailAddress}
                                        onChange={e => handleValueChange('emailAddress', e.target.value)}
                                        errors={errors.emailAddress}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 50,
                                        }}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="employer"
                                        id="employer"
                                        label="Employer"
                                        value={props.formState.employer}
                                        onChange={e => handleValueChange('employer', e.target.value)}
                                        errors={errors.employer}
                                        inputRef={register}
                                        inputProps={{
                                            maxLength: 100,
                                        }}
                                    />
                                </Form.Group>

                                {props.formState.state && !employmentStatusExcludedState.includes(props.formState.state) &&
                                    <Form.Group>
                                        <p className="section-title">Are you, the Proposed Insured, currently reporting to work (not out on leave, FML, disability, hiatus, or layoff) with the employer listed on this application?</p>
                                        <StyledRadioInput
                                            name="employmentStatus"
                                            id="employmentStatus"
                                            value={props.formState.employmentStatus}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('employmentStatus', e)}
                                            errors={errors && errors.employmentStatus}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </Form.Group>
                                }

                            </Col>
                        </Row>

                        <div className='backnext-wrapper'>
                            <BackNext onBack={props.onBack} isAflac={true} continueLabel={{ text: "Next" }} nextDisabled={getDisabled} />
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
}

InsuredInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
    basicInfo: PropTypes.object,
};

InsuredInfo.ValidationSchema = object().shape({
    pin: string()
        .required('PIN is required.')
        .length(4)
        .matches(/\d{4}/, 'PIN must be 4 numeric digits.'),
    pinConfirm: string()
        .required('PIN confirm is required.')
        .oneOf([ref('pin')], 'PINs must match.'),
    firstName: customValidators.string().isValidNameWithFiftyChar('First name is required.', true),
    middleInitial: customValidators.string().isValidNameWithFiftyChar('Middle Initials is required', false),
    lastName: customValidators.string().isValidNameWithFiftyChar('Last name is required.', true),
    street: customValidators.string().isValidNameLimitedSpecialChar('Street is required.', true, true),
    city: string().isValidNameLimitedSpecialChar('City is required.', true, false),
    state: string().required("State is required."),
    zip: string().required("Zip is required").matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
    corporateCityLimits: string().when('state', {
        is: "KY",
        then: string().required('This field is required.'),
        otherwise: string().notRequired()
    }),
    phoneNumber: string()
        .required('Phone Number is required.')
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
    emailAddress: string()
        .email('Invalid email')
        .required('Email is required.'),
    employer: string().required("Employer is required."),
    employmentStatus: string().when('state', {
        is: (state) => state && !employmentStatusExcludedState.includes(state),
        then: string().required("This field is required."),
        otherwise: string().notRequired()
    })
});

InsuredInfo.DefaultFormState = (prefill = false, basicInfo = null) => {
    return {
        pin: prefill ? "1111" : "",
        pinConfirm: prefill ? "1111" : "",
        firstName: prefill ? "Proposed" : (basicInfo && basicInfo.firstName) || '',
        middleInitial: prefill ? "N" : '',
        lastName: prefill ? "Sured" : (basicInfo && basicInfo.lastName) || '',
        street: prefill ? "2020 Covr St" : '',
        city: prefill ? "Boise" : '',
        state: prefill ? "CA" : (basicInfo && basicInfo.state) || '',
        zip: prefill ? "83702" : '',
        corporateCityLimits: '',
        phoneNumber: prefill ? "(769) 345-4567" : (basicInfo && basicInfo.phone) || '',
        emailAddress: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
        employer: prefill ? "Employer" : '',
        employmentStatus: ''
    };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Aflac/steps/InsuredInfo.js